import { Injectable, inject } from '@angular/core';
import { NativeService } from './native.service';
import { Settings } from '@app/app.settings';
import { CookieManager } from '../utils/cookie-manager';
import { LoggerService } from './logger.service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void;
  }
}

export interface ConsentSettings {
  ad_storage: string;
  analytics_storage: string;
  ad_user_data: string;
  ad_personalization: string;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private nativeService = inject(NativeService);
  private logger = inject(LoggerService);

  private readonly CONSENT_COOKIE_NAME = 'cookie_consent_level';

  private scriptPromise: Promise<void>;

  private consentSettings: ConsentSettings = {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  };

  constructor() {
    if (!this.nativeService.isNative()) {
      this.initializeDataLayer();
      this.addScript();
      this.addConsentGranted();
      this.initGoogleTagManager();
    } else {
      this.scriptPromise = Promise.resolve();
    }
  }

  public getScriptLoadPromise(): Promise<unknown> {
    return this.scriptPromise;
  }

  private initializeDataLayer() {
    window.dataLayer = window.dataLayer || [];

    window.gtag = function () {
      // eslint-disable-next-line
      window.dataLayer.push(arguments);
    };

    if (this.getConsentCookie()) {
      try {
        this.updateConsentFromCookie();
      } catch (e) {
        this.logger.error('Failed to parse consent cookie:', e);
        this.setDefaultConsent(this.consentSettings);
      }
    } else {
      this.setDefaultConsent(this.consentSettings);
    }
  }

  private async initGoogleTagManager() {
    try {
      await this.getScriptLoadPromise();
      this.logger.log('GTM script loaded successfully');
    } catch (error) {
      this.logger.error('Failed to load GTM script:', error);
    }
  }

  private getConsentCookie() {
    try {
      return JSON.parse(CookieManager.getCookie(this.CONSENT_COOKIE_NAME));
    } catch {
      return null;
    }
  }

  public consentGranted() {
    this.updateConsentFromCookie();
  }

  public updateConsentFromCookie() {
    const consentCookie = this.getConsentCookie();

    if (consentCookie.targeting) {
      this.consentSettings.ad_storage = 'granted';
      this.consentSettings.analytics_storage = 'granted';
      this.consentSettings.ad_user_data = 'granted';
      this.consentSettings.ad_personalization = 'granted';
    }

    window.gtag('consent', 'update', this.consentSettings);
  }

  private setDefaultConsent(consentSettings: ConsentSettings) {
    window.gtag('consent', 'default', consentSettings);
  }

  private addConsentGranted() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).consentGranted = this.consentGranted.bind(this);
  }

  private addScript() {
    this.scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');

      s.setAttribute('src', `https://www.googletagmanager.com/gtag/js?id=${Settings.GTMtagId}`);
      s.addEventListener('load', () => {
        window.gtag('js', new Date());
        window.gtag('config', Settings.GTMtagId);
        resolve();
      });
      s.addEventListener('error', reject);

      document.head.appendChild(s);
    });
  }
}
